import { type FormEvent, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, apiFormData } from '@/lib/api';
import { h5pSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface ImportResult {
  id: string;
  title: string;
  slug: string;
}

interface Category {
  id: string;
  name: string;
}

export function H5pImportPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [isDownloadable, setIsDownloadable] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api<{ data: Category[] } | Category[]>('/categories'),
  });

  const categoryRows = Array.isArray(categories) ? categories : (categories?.data ?? []);

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) {
        throw new Error('Debes seleccionar un archivo .h5p.');
      }

      const formData = new FormData();
      formData.append('package', selectedFile);
      if (title.trim()) formData.append('title', title.trim());
      if (description.trim()) formData.append('description', description.trim());

      return apiFormData<ImportResult>('/h5p/import', formData);
    },
    onSuccess: (pkg) => {
      toast(h5pSuccessToast('create'));
      void queryClient.invalidateQueries({ queryKey: ['h5p'] });
      navigate(`/h5p/${pkg.id}`);
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo crear el H5P'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    importMutation.mutate();
  }

  function handleFileChange() {
    const file = fileInputRef.current?.files?.[0] ?? null;
    setSelectedFile(file);
  }

  return (
    <div className="lms-h5p-import lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to="/h5p" label="Volver a Biblioteca H5P" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Importar paquete .h5p</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            El sistema valida el paquete, extrae su contenido y registra automáticamente sus librerías y
            dependencias.
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="lms-h5p-import__card">
        <div className="lms-h5p-import__body">
          <div className="lms-h5p-import__dropzone">
            <input
              ref={fileInputRef}
              type="file"
              name="package"
              accept=".h5p,.zip"
              required
              onChange={handleFileChange}
            />
            <p className="lms-h5p-import__dropzone-hint">
              Formato .h5p (ZIP con h5p.json). Tamaño máximo 100 MB.
            </p>
            {selectedFile ? (
              <p className="lms-h5p-import__dropzone-file">
                {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </p>
            ) : null}
          </div>

          <div className="lms-h5p-import__grid-2">
            <div className="lms-h5p-import__field">
              <label htmlFor="h5p-import-title" className="lms-h5p-import__label">
                Título (opcional)
              </label>
              <input
                id="h5p-import-title"
                type="text"
                name="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Se toma del paquete si se deja vacío"
                className="lms-h5p-import__input"
              />
            </div>

            <div className="lms-h5p-import__field">
              <label htmlFor="h5p-import-category" className="lms-h5p-import__label">
                Categoría
              </label>
              <select
                id="h5p-import-category"
                name="category_id"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="lms-h5p-import__select"
              >
                <option value="">Sin categoría</option>
                {categoryRows.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="lms-h5p-import__field">
            <label htmlFor="h5p-import-description" className="lms-h5p-import__label">
              Descripción
            </label>
            <textarea
              id="h5p-import-description"
              name="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="lms-h5p-import__textarea"
            />
          </div>

          <div className="lms-h5p-import__field">
            <label htmlFor="h5p-import-tags" className="lms-h5p-import__label">
              Etiquetas
            </label>
            <input
              id="h5p-import-tags"
              type="text"
              name="tags_input"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="Separadas por coma"
              className="lms-h5p-import__input"
            />
          </div>

          <div className="lms-h5p-import__grid-3">
            <label className="lms-h5p-import__checkbox">
              <input
                type="checkbox"
                name="is_visible"
                checked={isVisible}
                onChange={(e) => setIsVisible(e.target.checked)}
              />
              <span>Visible</span>
            </label>
            <label className="lms-h5p-import__checkbox">
              <input
                type="checkbox"
                name="is_downloadable"
                checked={isDownloadable}
                onChange={(e) => setIsDownloadable(e.target.checked)}
              />
              <span>Descargable</span>
            </label>
            <label className="lms-h5p-import__checkbox">
              <input
                type="checkbox"
                name="is_public"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span>Público</span>
            </label>
          </div>

          <div className="lms-h5p-import__footer">
            <Link to="/h5p" className="lms-h5p-library__btn lms-h5p-library__btn--outline">
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={importMutation.isPending || !selectedFile}
              className="lms-h5p-library__btn lms-h5p-library__btn--primary"
            >
              {importMutation.isPending ? 'Importando…' : 'Importar actividad'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
